import React, { useEffect, useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router } from "expo-router";
import { useSelector, useDispatch } from "react-redux";
import { useFocusEffect } from "expo-router";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";
import { RootState } from "../../store";
import { appActions } from "../../store/slices/appSlice";

// Import settings image for header
import SettingsImage from "../../assets/images/settings.png";

interface UserLimits {
  error?: string;
  productCount?: number;
  debtCount?: number;
  factureCount?: number;
}

interface LimitItemProps {
  title: string;
  used: number;
  limit: number;
  color: string;
  icon: string;
}

const LimitItem: React.FC<LimitItemProps> = ({
  title,
  used,
  limit,
  color,
  icon,
}) => {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const remaining = limit > 0 ? Math.max(limit - used, 0) : 0;

  const getStatusColor = () => {
    if (percentage >= 100) return "#EF4444";
    if (percentage >= 90) return "#F59E0B";
    return "#10B981";
  };

  return (
    <View style={styles.limitItem}>
      <View style={styles.limitHeader}>
        <View style={styles.limitInfo}>
          <Text style={styles.limitValue}>
            {used} / {limit === 0 ? i18n.t("unlimited") : limit}
          </Text>
          <Text style={[styles.limitPercentage, { color: getStatusColor() }]}>
            {percentage.toFixed(0)}%
          </Text>
        </View>
        <View style={styles.limitTitleContainer}>
          <Text style={styles.limitTitle}>{title}</Text>
          <View
            style={[
              styles.limitIconContainer,
              { backgroundColor: color + "20" },
            ]}
          >
            <Text style={styles.limitIcon}>{icon}</Text>
          </View>
        </View>
      </View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBackground}>
          <View
            style={[
              styles.progressBar,
              { width: `${percentage}%`, backgroundColor: getStatusColor() },
            ]}
          />
        </View>
        <View style={styles.progressLabels}>
          <Text style={styles.progressLabel}>
            {i18n.t("remainingLimit")}: {remaining}
          </Text>
          <Text style={styles.progressLabel}>
            {i18n.t("used")}: {used}
          </Text>
        </View>
      </View>
    </View>
  );
};

// Module-level cache for limits data (persists across component remounts)
let cachedLimits: UserLimits | null = null;

const WebLimitsScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);
  const { user } = useSelector((state: RootState) => state.auth);
  // Initialize with cached data if available
  const [limits, setLimits] = useState<UserLimits | null>(cachedLimits);
  const [loading, setLoading] = useState(cachedLimits === null);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const userData = user as {
    payed?: boolean;
    username?: string;
  } | null;

  const isPremium = userData?.payed || false;

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  // Fetch user limits
  const fetchUserLimits = async () => {
    try {
      setLoading(true);
      const response = await customAxios.get("/users/limite", {
        withCredentials: true,
      });
      const limitsData = response.data?.limite || response.data;
      setLimits(limitsData);
      cachedLimits = limitsData; // Save to module-level cache
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const limitsData = error.response.data?.limite || error.response.data;
        if (limitsData) {
          const cachedData = {
            productCount: limitsData.productCount || 0,
            debtCount: limitsData.debtCount || 0,
            factureCount: limitsData.factureCount || 0,
            error: error.response.data?.error,
          };
          setLimits(cachedData);
          cachedLimits = cachedData; // Save to module-level cache
        }
      } else {
        // silently ignore
      }
    } finally {
      setLoading(false);
    }
  };

  // Only fetch limits if not already loaded (caching)
  useEffect(() => {
    // If limits already exist from a previous fetch, skip the loading
    if (limits && limits.productCount !== undefined) {
      setLoading(false);
      return;
    }
    fetchUserLimits();
  }, []);

  const handleGoBack = () => {
    router.push("/(drawer)/params" as any);
  };

  const handleUpgrade = () => {
    router.push("/(drawer)/params/contact?upgrade=true" as any);
  };

  const limitItems = [
    {
      icon: "📦",
      title: i18n.t("productsUsage"),
      used: limits?.productCount || 0,
      limit: isPremium ? 0 : 50,
      color: "#3B82F6",
    },
    {
      icon: "💰",
      title: i18n.t("debtsUsage"),
      used: limits?.debtCount || 0,
      limit: isPremium ? 0 : 50,
      color: "#10B981",
    },
    {
      icon: "🧾",
      title: i18n.t("invoicesUsage"),
      used: limits?.factureCount || 0,
      limit: isPremium ? 0 : 50,
      color: "#8B5CF6",
    },
  ];

  return (
    <div
      key={`limits-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with breadcrumb and blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={SettingsImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={handleGoBack}>
                  <Text style={styles.breadcrumbLink}>
                    {i18n.t("settings")}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSeparator}>{"  >  "}</Text>
                <Text style={styles.breadcrumbCurrent}>
                  {i18n.t("limits")}
                </Text>
              </View>
            </div>
            <View style={styles.headerBar} />
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#1379A2" />
            </View>
          ) : (
            <>
              {/* Current Plan Card */}
              <View style={styles.planCard}>
                <View style={styles.planHeader}>
                  <View
                    style={[
                      styles.planBadge,
                      { backgroundColor: isPremium ? "#10B981" : "#F59E0B" },
                    ]}
                  >
                    <Text style={styles.planBadgeIcon}>
                      {isPremium ? "⭐" : "🆓"}
                    </Text>
                  </View>
                  <View style={styles.planInfo}>
                    <Text style={styles.planTitle}>
                      {i18n.t("currentLimitsTitle")}
                    </Text>
                    <Text style={styles.planDescription}>
                      {i18n.t("limitsDescription")}
                    </Text>
                  </View>
                </View>
                <View
                  style={[
                    styles.planStatus,
                    { backgroundColor: isPremium ? "#ECFDF5" : "#FFFBEB" },
                  ]}
                >
                  <Text
                    style={[
                      styles.planStatusText,
                      { color: isPremium ? "#059669" : "#D97706" },
                    ]}
                  >
                    {isPremium
                      ? i18n.t("premiumPlan")
                      : i18n.t("freePlan")}
                  </Text>
                </View>
              </View>

              {/* Usage Limits Section */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {i18n.t("usageLimits")}
                </Text>
                {limitItems.map((item, index) => (
                  <LimitItem
                    key={index}
                    icon={item.icon}
                    title={item.title}
                    used={item.used}
                    limit={item.limit}
                    color={item.color}
                  />
                ))}
              </View>

              {/* Upgrade Button for free users */}
              {!isPremium && (
                <TouchableOpacity
                  style={styles.upgradeButton}
                  onPress={handleUpgrade}
                >
                  <Text style={styles.upgradeButtonIcon}>⭐</Text>
                  <Text style={styles.upgradeButtonText}>
                    {i18n.t("upgradeNow")}
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 30,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbLink: {
    fontSize: 28,
    color: "#333",
  },
  breadcrumbSeparator: {
    fontSize: 28,
    color: "#999",
    marginHorizontal: 4,
  },
  breadcrumbCurrent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 60,
  },
  planCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
    marginHorizontal: 40,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  planHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    marginBottom: 16,
  },
  planBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: "center",
    justifyContent: "center",
  },
  planBadgeIcon: {
    fontSize: 28,
  },
  planInfo: {
    alignItems: "flex-end",
  },
  planTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#333",
    textAlign: "right",
  },
  planDescription: {
    fontSize: 16,
    color: "#666",
    marginTop: 4,
    textAlign: "right",
  },
  planStatus: {
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  planStatusText: {
    fontSize: 18,
    fontWeight: "600",
  },
  section: {
    marginHorizontal: 40,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#333",
    textAlign: "right",
    marginBottom: 16,
  },
  limitItem: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  limitHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  limitTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  limitIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  limitIcon: {
    fontSize: 22,
  },
  limitTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  limitInfo: {
    alignItems: "flex-start",
  },
  limitValue: {
    fontSize: 16,
    color: "#666",
  },
  limitPercentage: {
    fontSize: 14,
    fontWeight: "600",
    marginTop: 2,
  },
  progressContainer: {
    marginTop: 8,
  },
  progressBackground: {
    height: 8,
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  progressLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  progressLabel: {
    fontSize: 14,
    color: "#666",
  },
  upgradeButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3B82F6",
    borderRadius: 30,
    paddingVertical: 16,
    paddingHorizontal: 32,
    marginHorizontal: 40,
    marginTop: 16,
    gap: 12,
  },
  upgradeButtonIcon: {
    fontSize: 22,
  },
  upgradeButtonText: {
    fontSize: 20,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default WebLimitsScreen;
